import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/feature/PageHeader";
import usePageMeta from "@/hooks/usePageMeta";
import { plotTimeline, keyNodes, siteName } from "@/mocks/mangaData";

export default function Plot() {
  usePageMeta({
    title: "甜蜜惩罚漫画剧情主线 | 分卷脉络与关键节点追踪 - 甜蜜惩罚漫画剧情追踪站",
    description:
      "甜蜜惩罚漫画主线走向全梳理：从初遇到终章的分卷脉络、关键分话节点与反转梳理，帮你快速看懂甜蜜惩罚漫画剧情主线与人物命运走向。",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "甜蜜惩罚漫画剧情主线",
        inLanguage: "zh-CN",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "/" },
            { "@type": "ListItem", position: 2, name: "剧情主线" },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: plotTimeline.map((node, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: node.title,
        })),
      },
    ],
  });

  return (
    <Layout>
      <PageHeader
        title="剧情主线 · 分卷脉络"
        subtitle="从一场交易式的相遇开始，到身世疑云与家族旧怨的层层展开。这里把《甜蜜惩罚漫画》的主线按分卷拆解，让你一眼看清剧情从何而来、将往何处。"
        crumbs={[
          { label: "首页", path: "/" },
          { label: "剧情主线" },
        ]}
      />

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 时间线主体 */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold mb-6">主线时间线</h2>
              <div className="flex flex-col">
                {plotTimeline.map((node, i) => (
                  <div key={node.phase} className="relative flex gap-5 pb-10 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary-500 text-background-50 font-heading text-sm font-bold">
                        {i + 1}
                      </span>
                      {i < plotTimeline.length - 1 && (
                        <span className="mt-2 w-px flex-1 bg-primary-200"></span>
                      )}
                    </div>
                    <div className="flex-1 rounded-lg border border-background-200 bg-background-50 p-6">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-label text-xs font-semibold text-primary-600 uppercase tracking-wider">
                          {node.phase}
                        </span>
                        <span className="rounded-full bg-secondary-100 text-secondary-900 px-3 py-1 text-xs font-medium">
                          {node.range}
                        </span>
                      </div>
                      <h3 className="mt-3 font-heading text-lg md:text-xl font-semibold text-foreground-950">
                        {node.title}
                      </h3>
                      <p className="mt-3 text-sm md:text-base leading-7 text-foreground-700">
                        {node.summary}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {node.tags.map((t) => (
                          <span
                            key={t}
                            className="rounded-full bg-background-200/70 text-foreground-600 px-3 py-1 text-xs"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 侧边栏 */}
            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-lg font-semibold mb-4">关键分话节点</h3>
                <ul className="flex flex-col gap-4">
                  {keyNodes.map((n) => (
                    <li key={n.chapter}>
                      <Link to="/chapters" className="group flex items-start gap-3">
                        <span className="font-heading text-sm font-bold text-primary-600 mt-0.5 whitespace-nowrap">
                          {n.chapter}
                        </span>
                        <span className="text-sm text-foreground-800 group-hover:text-primary-700 transition-colors">
                          {n.title}
                        </span>
                      </Link>
                      <p className="mt-1 text-xs leading-5 text-foreground-500">{n.desc}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-accent-200 bg-accent-50 p-6">
                <h3 className="font-heading text-lg font-semibold text-accent-900 mb-3">
                  <i className="ri-lightbulb-flash-line mr-2"></i>
                  追更提示
                </h3>
                <p className="text-sm leading-7 text-accent-800">
                  主线已进入终章收束阶段，结局线索持续更新中。建议按时间线顺序补齐关键分话，再看本页侧栏的关键节点，剧情脉络会清晰很多。
                </p>
                <Link
                  to="/chapters"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent-700 hover:text-accent-800"
                >
                  前往分话解析
                  <i className="ri-arrow-right-line text-base leading-none"></i>
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="font-heading text-lg font-semibold mb-3">更多内容</h3>
                <ul className="flex flex-col gap-2.5 text-sm">
                  <li>
                    <Link to="/characters" className="inline-flex items-center gap-2 text-foreground-800 hover:text-primary-700">
                      <i className="ri-user-heart-line text-primary-600"></i>
                      角色解析与人物命运
                    </Link>
                  </li>
                  <li>
                    <Link to="/about" className="inline-flex items-center gap-2 text-foreground-800 hover:text-primary-700">
                      <i className="ri-book-open-line text-primary-600"></i>
                      关于本站与内容声明
                    </Link>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </Layout>
  );
}